import { useState, useEffect, useCallback } from "react";
import { Accessibility, Plus, Minus, RotateCcw, X, Type, Eye, MousePointer, Link2, Pause, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const defaultSettings = {
  textSize: 0,
  highContrast: false,
  highlightLinks: false,
  grayscale: false,
  largeCursor: false,
  stopAnimations: false,
  lineHeight: 0,
  letterSpacing: 0,
};

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem("a11y-settings");
      return saved ? JSON.parse(saved) : defaultSettings;
    } catch {
      return defaultSettings;
    }
  });

  const applySettings = useCallback((s) => {
    const root = document.documentElement;
    const body = document.body;

    // Text size
    root.style.fontSize = s.textSize === 0 ? "" : `${100 + s.textSize * 15}%`;

    // High contrast
    if (s.highContrast) {
      body.classList.add("a11y-high-contrast");
    } else {
      body.classList.remove("a11y-high-contrast");
    }

    // Highlight links
    if (s.highlightLinks) {
      body.classList.add("a11y-highlight-links");
    } else {
      body.classList.remove("a11y-highlight-links");
    }

    // Grayscale
    if (s.grayscale) {
      body.classList.add("a11y-grayscale");
    } else {
      body.classList.remove("a11y-grayscale");
    }

    // Large cursor
    if (s.largeCursor) {
      body.classList.add("a11y-large-cursor");
    } else {
      body.classList.remove("a11y-large-cursor");
    }

    // Stop animations
    if (s.stopAnimations) {
      body.classList.add("a11y-stop-animations");
    } else {
      body.classList.remove("a11y-stop-animations");
    }

    // Line height
    root.style.setProperty("--a11y-line-height", s.lineHeight === 0 ? "" : `${1.5 + s.lineHeight * 0.3}`);
    if (s.lineHeight > 0) {
      body.classList.add("a11y-line-height");
    } else {
      body.classList.remove("a11y-line-height");
    }

    // Letter spacing
    root.style.setProperty("--a11y-letter-spacing", s.letterSpacing === 0 ? "" : `${s.letterSpacing * 0.5}px`);
    if (s.letterSpacing > 0) {
      body.classList.add("a11y-letter-spacing");
    } else {
      body.classList.remove("a11y-letter-spacing");
    }
  }, []);

  useEffect(() => {
    applySettings(settings);
    localStorage.setItem("a11y-settings", JSON.stringify(settings));
  }, [settings, applySettings]);

  const update = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const reset = () => {
    setSettings(defaultSettings);
  };

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(defaultSettings);

  return (
    <>
      {/* Floating button */}
      <button
        data-testid="accessibility-widget-btn"
        aria-label="Accessibility Options"
        onClick={() => setOpen(!open)}
        className="fixed bottom-5 left-5 z-[9999] w-12 h-12 rounded-full bg-sky-600 hover:bg-sky-700 text-white shadow-lg hover:shadow-xl flex items-center justify-center transition-all duration-200 hover:scale-105"
      >
        <Accessibility className="h-6 w-6" />
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="accessibility-panel"
          className="fixed bottom-20 left-5 z-[9999] w-80 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
        >
          {/* Header */}
          <div className="bg-sky-600 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-white">
              <Accessibility className="h-5 w-5" />
              <span className="text-sm font-semibold">Accessibility Settings</span>
            </div>
            <button
              data-testid="accessibility-close-btn"
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
            {/* Text Size */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Type className="h-4 w-4 text-slate-500" />
                <span className="text-sm text-slate-700">Text Size</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid="a11y-text-decrease"
                  onClick={() => update("textSize", Math.max(-2, settings.textSize - 1))}
                  className="w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                >
                  <Minus className="h-3 w-3" />
                </button>
                <span className="text-xs font-medium text-slate-600 w-8 text-center">
                  {settings.textSize === 0 ? "0" : `${settings.textSize > 0 ? "+" : ""}${settings.textSize}`}
                </span>
                <button
                  data-testid="a11y-text-increase"
                  onClick={() => update("textSize", Math.min(4, settings.textSize + 1))}
                  className="w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                >
                  <Plus className="h-3 w-3" />
                </button>
              </div>
            </div>

            {/* Line Height */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Type className="h-4 w-4 text-slate-500" />
                <span className="text-sm text-slate-700">Line Height</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid="a11y-lineheight-decrease"
                  onClick={() => update("lineHeight", Math.max(0, settings.lineHeight - 1))}
                  className="w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                >
                  <Minus className="h-3 w-3" />
                </button>
                <span className="text-xs font-medium text-slate-600 w-8 text-center">{settings.lineHeight}</span>
                <button
                  data-testid="a11y-lineheight-increase"
                  onClick={() => update("lineHeight", Math.min(3, settings.lineHeight + 1))}
                  className="w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                >
                  <Plus className="h-3 w-3" />
                </button>
              </div>
            </div>

            {/* Letter Spacing */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Type className="h-4 w-4 text-slate-500" />
                <span className="text-sm text-slate-700">Letter Spacing</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid="a11y-spacing-decrease"
                  onClick={() => update("letterSpacing", Math.max(0, settings.letterSpacing - 1))}
                  className="w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                >
                  <Minus className="h-3 w-3" />
                </button>
                <span className="text-xs font-medium text-slate-600 w-8 text-center">{settings.letterSpacing}</span>
                <button
                  data-testid="a11y-spacing-increase"
                  onClick={() => update("letterSpacing", Math.min(3, settings.letterSpacing + 1))}
                  className="w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                >
                  <Plus className="h-3 w-3" />
                </button>
              </div>
            </div>

            <Separator />

            {/* Toggle options */}
            {[
              { key: "highContrast", icon: Sun, label: "High Contrast", testId: "a11y-contrast" },
              { key: "highlightLinks", icon: Link2, label: "Highlight Links", testId: "a11y-links" },
              { key: "grayscale", icon: Eye, label: "Grayscale", testId: "a11y-grayscale" },
              { key: "largeCursor", icon: MousePointer, label: "Large Cursor", testId: "a11y-cursor" },
              { key: "stopAnimations", icon: Pause, label: "Stop Animations", testId: "a11y-animations" },
            ].map((opt) => (
              <button
                key={opt.key}
                data-testid={opt.testId}
                onClick={() => update(opt.key, !settings[opt.key])}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${
                  settings[opt.key]
                    ? "bg-sky-50 border border-sky-200"
                    : "bg-slate-50 border border-transparent hover:bg-slate-100"
                }`}
              >
                <div className="flex items-center gap-2">
                  <opt.icon className={`h-4 w-4 ${settings[opt.key] ? "text-sky-600" : "text-slate-500"}`} />
                  <span className={`text-sm ${settings[opt.key] ? "text-sky-700 font-medium" : "text-slate-700"}`}>
                    {opt.label}
                  </span>
                </div>
                <div
                  className={`w-9 h-5 rounded-full transition-colors flex items-center px-0.5 ${
                    settings[opt.key] ? "bg-sky-600" : "bg-slate-300"
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
                      settings[opt.key] ? "translate-x-4" : "translate-x-0"
                    }`}
                  />
                </div>
              </button>
            ))}

            <Separator />

            {/* Reset + Statement link */}
            <div className="flex items-center gap-2">
              {hasChanges && (
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="a11y-reset"
                  onClick={reset}
                  className="flex-1 text-xs border-slate-300"
                >
                  <RotateCcw className="h-3 w-3 mr-1" /> Reset All
                </Button>
              )}
              <a
                href="/accessibility"
                data-testid="a11y-statement-link"
                className="flex-1 text-center text-xs text-sky-600 hover:text-sky-700 font-medium py-2"
              >
                Accessibility Statement
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
